import { useCallback, useEffect, useRef } from 'react';

/**
 * ## Why Runs and Schedules poll instead of subscribing
 *
 * The house rule is to subscribe to the realtime hooks rather than poll — but
 * `src/modules/reporting/` emits nothing at all: no `VENUE_EVENTS` entry for a
 * run finishing, a schedule suspending itself, or a delivery landing. It is a
 * `@Cron`-ticked batch system (`SCHEDULE_BATCH` due schedules per tick), not a
 * request/response one, so there is no event to have missed wiring up — unlike
 * the fleet epic's `useAutoRefresh`, which polls because the events exist on
 * the backend and simply are not forwarded to a socket yet.
 *
 * Same two properties as that one, so an operator who leaves the Runs tab open
 * waiting for a 6am report is not a request every few seconds forever:
 *  - stops while the tab is hidden;
 *  - skips a tick while the previous reload is still in flight.
 */
export function useAutoRefresh(
  reload: () => void,
  { enabled, intervalMs }: { enabled: boolean; intervalMs: number },
) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const inFlight = useRef(false);

  const tick = useCallback(() => {
    if (inFlight.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    inFlight.current = true;
    try {
      reloadRef.current();
    } finally {
      window.setTimeout(() => {
        inFlight.current = false;
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [enabled, intervalMs, tick]);
}

/** Shared by every ADR-0013 screen in this folder — the same shape Settings, Platform and Spaces each keep their own copy of. */
export function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' · ');
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

export function fullName(person: { firstName?: string | null; lastName?: string | null }): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ');
}

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

/**
 * `ReportScheduleStatus` and `ReportRunStatus` are not part of the shared
 * status-enum union `components/ui/badge.tsx` type-checks against, so this is
 * a small local table rather than an entry added to that one — the same
 * choice every screen with its own enum outside that closed set makes.
 */
export function scheduleStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PAUSED':
      return 'warning';
    // The system stopped it, not a person — the loudest of the three.
    case 'SUSPENDED':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function runStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'PENDING':
      return 'warning';
    case 'RUNNING':
      return 'info';
    case 'SUCCEEDED':
      return 'success';
    case 'FAILED':
      return 'danger';
    // Ran, but nobody received it — a visible state and deliberately not a success.
    case 'DELIVERED_NONE':
      return 'danger';
    default:
      return 'neutral';
  }
}
